import { l } from '@manychat/manyui'

import { UpgradeSource, billing } from 'common/billing'
import { alert } from 'common/core'
import { IAsyncThunkAction, IAsyncThunkActionWithReturnValue } from 'common/core/interfaces/actions'
import * as atypes from 'common/groups/constants/GroupsReduxActionTypes'
import { handleCatch } from 'shared/api/lib/errors/handlers'
import { applyHandlerByGuard, reportUnhandledErrors } from 'shared/api/lib/errors/utils'
import { GroupsApi } from 'shared/api/requests/groups'
import { getHash, navigatePollyfill } from 'utils/router/tools'

export const fetchGroupsList = (): IAsyncThunkAction => async (dispatch, getState) => {
  try {
    const store = getState()
    if (store.groups.isFetching) return

    dispatch({ type: atypes.GROUPS_FETCH_GROUPS_LIST_REQUEST })

    const result = await GroupsApi.list()
    dispatch({ type: atypes.GROUPS_FETCH_GROUPS_LIST_RESPONSE, groups: result.data.groups })
  } catch (error) {
    dispatch({ type: atypes.GROUPS_FETCH_GROUPS_LIST_ERROR })
    handleCatch(error)
  }
}

export const createGroup =
  (name: string, userIds: number[] = []): IAsyncThunkAction =>
  async (dispatch) => {
    const hash = getHash()

    try {
      dispatch({ type: atypes.GROUPS_CREATE_GROUP_REQUEST })

      const result = await GroupsApi.create({ body: { name, user_ids: userIds } })
      const group = result.data?.group

      dispatch({ type: atypes.GROUPS_CREATE_GROUP_RESPONSE, group })
      if (group.group_id) {
        navigatePollyfill(`?groupId=${group.group_id}${hash}`)
        alert(l.translate('Group successfully created!'), 'success')
      }
    } catch (error) {
      dispatch({ type: atypes.GROUPS_CREATE_GROUP_ERROR })
      handleCatch(error)
    }
  }

export const deleteGroup =
  (groupId: number): IAsyncThunkAction =>
  async (dispatch) => {
    try {
      dispatch({ type: atypes.GROUPS_DELETE_GROUP_REQUEST })
      await GroupsApi.delete({ body: { group_id: groupId } })
      dispatch({ type: atypes.GROUPS_DELETE_GROUP_RESPONSE, groupId })
      alert(l.translate('Group successfully deleted!'), 'success')
    } catch (error) {
      dispatch({ type: atypes.GROUPS_DELETE_GROUP_ERROR })
      handleCatch(error)
    }
  }

export const updateGroupName =
  (groupId: number, name: string): IAsyncThunkActionWithReturnValue<boolean> =>
  async (dispatch) => {
    try {
      dispatch({ type: atypes.GROUPS_UPDATE_GROUP_NAME_REQUEST })
      await GroupsApi.update({ body: { group_id: groupId, name } })
      dispatch({ type: atypes.GROUPS_UPDATE_GROUP_NAME_RESPONSE, groupId, name })
      alert(l.translate('Group name successfully changed!'), 'success')

      return true
    } catch (error) {
      dispatch({ type: atypes.GROUPS_UPDATE_GROUP_NAME_ERROR })
      handleCatch(error)
      return false
    }
  }

export const switchAutoAssign =
  (groupId: number, isEnabled: boolean): IAsyncThunkAction =>
  async (dispatch) => {
    try {
      dispatch({ type: atypes.GROUPS_SWITCH_AUTO_ASSIGN_REQUEST })
      await GroupsApi.switchAutoAssign({
        body: { group_id: groupId, is_enabled: isEnabled },
      })
      dispatch({ type: atypes.GROUPS_SWITCH_AUTO_ASSIGN_RESPONSE, groupId, isEnabled })
      alert(
        l.translate('Conversations Assignment {statusText}!', {
          statusText: isEnabled ? 'enabled' : 'disabled',
        }),
        'success',
      )
    } catch (error) {
      dispatch({ type: atypes.GROUPS_SWITCH_AUTO_ASSIGN_ERROR })

      handleCatch(error, (responseError) => {
        const unhandled = applyHandlerByGuard(
          responseError.$errors,
          billing.isProError,
          (error) => {
            billing.handleProError(error, {
              showPaywall: true,
              source: UpgradeSource.SETTINGS_TEAM,
            })
          },
        )

        reportUnhandledErrors(unhandled, responseError.endpoint)
      })
    }
  }

export const changeUsers =
  (groupId: number, userIds: number[]): IAsyncThunkAction =>
  async (dispatch) => {
    try {
      dispatch({ type: atypes.GROUPS_CHANGE_USERS_REQUEST })
      await GroupsApi.changeUsers({ body: { group_id: groupId, user_ids: userIds } })
      dispatch({ type: atypes.GROUPS_CHANGE_USERS_RESPONSE, groupId, userIds })
      alert(l.translate('Group members successfully changed!'), 'success')
    } catch (error) {
      dispatch({ type: atypes.GROUPS_CHANGE_USERS_ERROR })
      handleCatch(error)
    }
  }
